package com.example.mystybag.common.ui.components.custom

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.interaction.collectIsFocusedAsState
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.relocation.BringIntoViewRequester
import androidx.compose.foundation.relocation.bringIntoViewRequester
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.text.selection.LocalTextSelectionColors
import androidx.compose.foundation.text.selection.TextSelectionColors
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import kotlinx.coroutines.delay
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.drawWithContent
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusProperties
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.focus.onFocusEvent
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.ColorFilter
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.platform.LocalFocusManager
import androidx.compose.ui.platform.LocalSoftwareKeyboardController
import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.OffsetMapping
import androidx.compose.ui.text.input.TransformedText
import androidx.compose.ui.text.input.TextFieldValue
import androidx.compose.ui.text.input.VisualTransformation
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import dev.icerock.moko.resources.ImageResource
import dev.icerock.moko.resources.compose.painterResource
import dev.icerock.moko.resources.compose.stringResource
import com.example.mystybag.common.resources.SR
import com.example.mystybag.common.extensions.Image
import com.example.mystybag.common.extensions.noRippleClickable
import com.example.mystybag.common.helpers.UtilsTransfersMethods.validateTextInput
import com.example.mystybag.core.model.UISearchCallbacks
import com.example.mystybag.core.resources.SRC
import com.example.mystybag.common.ui.components.native.Texts
import com.example.mystybag.core.defines.TextFieldType
import com.example.mystybag.core.ui.theme.LocalTheme.carosoft
import com.example.mystybag.core.ui.theme.LocalTheme.p0
import com.example.mystybag.core.ui.theme.LocalTheme.p1
import com.example.mystybag.core.ui.theme.LocalTheme.p12
import com.example.mystybag.core.ui.theme.LocalTheme.p10
import com.example.mystybag.core.ui.theme.LocalTheme.p16
import com.example.mystybag.core.ui.theme.LocalTheme.p18
import com.example.mystybag.core.ui.theme.LocalTheme.p2
import com.example.mystybag.core.ui.theme.LocalTheme.p24
import com.example.mystybag.core.ui.theme.LocalTheme.p32
import com.example.mystybag.core.ui.theme.LocalTheme.p4
import com.example.mystybag.core.ui.theme.LocalTheme.p44
import com.example.mystybag.core.ui.theme.LocalTheme.p48
import com.example.mystybag.core.ui.theme.LocalTheme.p20
import com.example.mystybag.core.ui.theme.LocalTheme.p54
import com.example.mystybag.core.ui.theme.LocalTheme.p56
import com.example.mystybag.core.ui.theme.LocalTheme.p6
import com.example.mystybag.core.ui.theme.LocalTheme.p8
import com.example.mystybag.core.ui.theme.LocalTheme.p9
import com.example.mystybag.core.ui.theme.defaultLightColorScheme
import com.example.mystybag.core.views.AppTheme
import com.example.mystybag.core.views.isDarkTheme

/**
 * Same visual as [androidx.compose.ui.text.input.PasswordVisualTransformation] (bullet per char)
 * but not recognized as password on iOS, so Paste stays in context menu when field is masked.
 */
object MaskedDotTransformation : VisualTransformation {
    override fun filter(text: AnnotatedString): TransformedText =
        TransformedText(
            AnnotatedString("\u2022".repeat(text.length)),
            OffsetMapping.Identity
        )
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun DefaultCircleShapedTextField(
    modifier                : Modifier                  = Modifier,
    value                   : String                    = "",
    onValueChange           : (String) -> Unit          = {},
    enabled                 : Boolean                   = true,
    readOnly                : Boolean                   = false,
    canFocus                : Boolean                   = true,
    textAlign               : TextAlign                 = TextAlign.Unspecified,
    label                   : String?                   = null,
    placeholder             : @Composable (() -> Unit)? = null,
    leadingIcon             : @Composable (() -> Unit)? = null,
    trailingIcon            : @Composable (() -> Unit)? = null,
    isError                 : Boolean                   = false,
    errorText               : String                    = "",
    errorLeadingIcon        : ImageResource?             = null,
    keyboardOptions         : KeyboardOptions           = KeyboardOptions.Default,
    keyboardActions         : KeyboardActions           = KeyboardActions.Default,
    singleLine              : Boolean                   = true,
    maxLines                : Int                       = Int.MAX_VALUE,
    alpha                   : Float                     = 1f,
    interactionSource       : MutableInteractionSource  = remember { MutableInteractionSource() },
    visualTransformation    : VisualTransformation      = VisualTransformation.None,
    inputType               : TextFieldType?            = null,
    backgroundColor         : Color?                    = null,
    borderColor             : Color?                    = backgroundColor,
    /** When [enabled] is false, color for the value text; label stays onSurfaceVariant. */
    disabledTextColor       : Color?                    = null,
    focusRequester          : FocusRequester            = remember { FocusRequester() },
    hasEndPadding           : Boolean                   = true,
) {
    val theme           = AppTheme.current
    val disabledValueColor = disabledTextColor ?: theme.colors.onSurfaceVariant
    var labelColor      by remember(isDarkTheme.value, enabled) { mutableStateOf(if (!enabled) theme.colors.onSurfaceVariant else theme.colors.primary) }
    var textColor       by remember(isDarkTheme.value, enabled, disabledTextColor) {
        mutableStateOf(if (!enabled) disabledValueColor else theme.colors.onSurface)
    }
    var cursorColor     by remember(isDarkTheme.value) { mutableStateOf(theme.colors.primary) }
    var borderColors    by remember(isDarkTheme.value, enabled) { mutableStateOf(borderColor ?: theme.colors.outline) }
    var labelPadding    by remember { mutableStateOf(if (value.isNotEmpty() && placeholder == null) p0 else p9) }
    var labelSize       by remember(isDarkTheme.value) { mutableStateOf(if (value.isNotEmpty() || leadingIcon != null) theme.typography.labelSmall else theme.typography.bodyLarge) }
    var isFocused       by remember { mutableStateOf(false) }
    var endPadding      by remember { mutableStateOf(if (hasEndPadding) p18 else p0) }
    val bringIntoViewRequester = remember { BringIntoViewRequester() }

    LaunchedEffect(isFocused) {
        if (isFocused) {
            kotlin.io.println("[KeyboardScroll] DefaultCircleShapedTextField focused, calling bringIntoView in 400ms")
            delay(400)
            bringIntoViewRequester.bringIntoView()
            kotlin.io.println("[KeyboardScroll] DefaultCircleShapedTextField bringIntoView() #1 done, calling #2 in 250ms")
            delay(250)
            bringIntoViewRequester.bringIntoView()
            kotlin.io.println("[KeyboardScroll] DefaultCircleShapedTextField bringIntoView() #2 done")
        }
    }
    LaunchedEffect(isFocused, value, placeholder, leadingIcon, enabled) {
        labelPadding = if ((isFocused || value.isNotEmpty()) && placeholder == null) p0 else p9
        labelSize = if (isFocused || value.isNotEmpty() || leadingIcon != null) theme.typography.labelSmall else theme.typography.bodyLarge
    }
    LaunchedEffect(enabled, disabledTextColor) {
        if (!enabled) {
            labelColor = theme.colors.onSurfaceVariant
            textColor = disabledValueColor
            borderColors = theme.colors.outline
        }
    }

    Column(
        horizontalAlignment = Alignment.Start,
        modifier            = modifier
            .fillMaxWidth()
            .bringIntoViewRequester(bringIntoViewRequester)
    ) {
        Box(
            modifier = Modifier
                .height(p56)
                .fillMaxWidth()
                .background(
                    color = backgroundColor ?: when {
                        !enabled -> AppTheme.current.colors.surfaceContainerLowest
                        readOnly -> AppTheme.current.colors.surfaceContainer
                        else -> AppTheme.current.colors.surface
                    },
                    shape = RoundedCornerShape(p20)
                )
                .border(
                    width = p1,
                    color = if (isError && enabled) defaultLightColorScheme.error else borderColors,
                    RoundedCornerShape(p20)
                )
                .padding(end = endPadding),
            contentAlignment = Alignment.CenterStart
        ) {
            BasicTextField(
                modifier        = Modifier
                    .fillMaxWidth()
                    .padding(start = p16, top = p10, end = trailingIcon?.let { p24 } ?: p18)
                    .offset(p0, p4)
                    .focusRequester(focusRequester)
                    .focusProperties { this.canFocus = canFocus }
                    .onFocusEvent {
                        isFocused = it.isFocused
                        labelColor      = when {
                            !enabled                    -> theme.colors.onSurfaceVariant
                            it.isFocused && !isError    -> theme.colors.primary
                            isError                     -> defaultLightColorScheme.error
                            else                        -> theme.colors.onSurfaceVariant
                        }
                        borderColors     = when {
                            !enabled                    -> theme.colors.outline
                            it.isFocused && !isError && !readOnly -> theme.colors.primary
                            isError && !readOnly        -> defaultLightColorScheme.error
                            else                        -> borderColor ?: theme.colors.outline
                        }
                        textColor       = when {
                            !enabled    -> disabledValueColor
                            isError     -> defaultLightColorScheme.error
                            else        -> theme.colors.onSurface
                        }
                        cursorColor     = if (it.isFocused && isError) defaultLightColorScheme.error else theme.colors.primary
                    },
                value             = value,
                onValueChange     = { onValueChange(inputType?.let { type -> validateTextInput(type, it) } ?: it) },
                readOnly          = readOnly,
                maxLines          = maxLines,
                enabled           = enabled,
                singleLine        = singleLine,
                interactionSource = interactionSource,
                keyboardActions   = keyboardActions,
                keyboardOptions   = keyboardOptions,
                cursorBrush       = SolidColor(cursorColor),
                textStyle         = TextStyle(
                    fontFamily      = carosoft,
                    fontWeight      = FontWeight.Light,
                    fontSize        = 16.sp,
                    lineHeight      = 24.sp,
                    letterSpacing   = 0.4.sp,
                    color           = textColor.copy(alpha = alpha),
                    textAlign       = textAlign,
                ),
                decorationBox     = { innerTextField ->
                    OutlinedTextFieldDefaults.DecorationBox(
                        value                = value,
                        innerTextField       = innerTextField,
                        visualTransformation = VisualTransformation.None,
                        singleLine           = singleLine,
                        enabled              = enabled,
                        interactionSource    = interactionSource,
                        label                = { label?.also { Texts.Label(text = it, color = labelColor, modifier = Modifier.offset(p0, -labelPadding), textStyle = labelSize) } },
                        contentPadding       = PaddingValues(bottom = p18),
                        placeholder          = placeholder,
                        container            = {},
                        prefix               = leadingIcon
                    )
                },
                visualTransformation = visualTransformation
            )

            trailingIcon?.also {
                Box(
                    modifier    = Modifier.align(Alignment.CenterEnd),
                    content     = { it() }
                )
            }
        }

        if (isError && errorText.isNotEmpty()) {
            Row(
                modifier    = Modifier.padding(start = p16, end = p16, top = p4),
                verticalAlignment = Alignment.CenterVertically
            ) {
                errorLeadingIcon?.let { icon ->
                    Image(
                        painter     = painterResource(icon),
                        modifier    = Modifier.size(p20),
                        colorFilter = ColorFilter.tint(AppTheme.current.colors.error)
                    )
                    Spacer(modifier = Modifier.size(p4))
                }
                Texts.Overline(
                    modifier    = Modifier,
                    text        = errorText,
                    color       = AppTheme.current.colors.error,
                    uppercased  = false
                )
            }
        }
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun DefaultCircleShapedTextFieldCursor(
    modifier                : Modifier                      = Modifier,
    textFieldModifier       : Modifier                      = Modifier,
    value                   : TextFieldValue                = TextFieldValue(),
    onValueChange           : (TextFieldValue) -> Unit      = { },
    enabled                 : Boolean                       = true,
    readOnly                : Boolean                       = false,
    canFocus                : Boolean                       = true,
    textAlign               : TextAlign                     = TextAlign.Unspecified,
    label                   : String?                       = null,
    placeholder             : @Composable (() -> Unit)?     = null,
    leadingIcon             : @Composable (() -> Unit)?     = null,
    trailingIcon            : @Composable (() -> Unit)?     = null,
    isError                 : Boolean                       = false,
    errorText               : String                        = "",
    errorLeadingIcon        : ImageResource?                = null,
    keyboardOptions         : KeyboardOptions               = KeyboardOptions.Default,
    keyboardActions         : KeyboardActions               = KeyboardActions.Default,
    singleLine              : Boolean                       = true,
    maxLines                : Int                           = Int.MAX_VALUE,
    alpha                   : Float                         = 1f,
    interactionSource       : MutableInteractionSource      = remember { MutableInteractionSource() },
    visualTransformation    : VisualTransformation          = VisualTransformation.None,
    inputType               : TextFieldType?                = null,
    focusRequester          : FocusRequester                = remember { FocusRequester() },
    onFocusChanged          : ((Boolean) -> Unit)?          = null,
    contentPaddingStart     : Dp?                          = null,
    contentPaddingEnd       : Dp?                          = null
) {
    val theme                = AppTheme.current
    val startPadding        = contentPaddingStart ?: p0
    val endPadding           = contentPaddingEnd ?: p18
    var labelColor           by remember(isDarkTheme.value, enabled) { mutableStateOf(if (!enabled) theme.colors.onSurfaceVariant else theme.colors.primary) }
    var textColor            by remember(isDarkTheme.value, enabled) { mutableStateOf(if (!enabled) theme.colors.onSurfaceVariant else theme.colors.onSurface) }
    var borderColor          by remember(isDarkTheme.value, enabled) { mutableStateOf(theme.colors.outline) }
    var cursorColor          by remember(isDarkTheme.value) { mutableStateOf(theme.colors.primary) }
    var labelPadding         by remember { mutableStateOf(if (value.text.isNotEmpty()) p4 else p9) }
    var labelSize            by remember(isDarkTheme.value) { mutableStateOf(if (value.text.isNotEmpty() || leadingIcon != null) theme.typography.labelSmall else theme.typography.bodyLarge) }
    var topContentPadding    by remember { mutableStateOf(if (!singleLine && value.text.isEmpty()) p9 else p0) }
    var isFocused            by remember { mutableStateOf(false) }
    val bringIntoViewRequester = remember { BringIntoViewRequester() }

    LaunchedEffect(isFocused) {
        if (isFocused) {
            kotlin.io.println("[KeyboardScroll] DefaultCircleShapedTextFieldCursor focused, calling bringIntoView in 400ms")
            delay(400)
            bringIntoViewRequester.bringIntoView()
            kotlin.io.println("[KeyboardScroll] DefaultCircleShapedTextFieldCursor bringIntoView() #1 done, calling #2 in 250ms")
            delay(250)
            bringIntoViewRequester.bringIntoView()
            kotlin.io.println("[KeyboardScroll] DefaultCircleShapedTextFieldCursor bringIntoView() #2 done")
        }
    }
    LaunchedEffect(isFocused, value.text, leadingIcon, enabled) {
        labelPadding = p8
        labelSize = if (isFocused || leadingIcon != null || value.text.isNotEmpty()) theme.typography.labelSmall else theme.typography.bodyLarge
        topContentPadding = if (!singleLine && (value.text.isNotEmpty() || isFocused)) p0 else p9
    }
    LaunchedEffect(enabled) {
        if (!enabled) {
            labelColor = theme.colors.onSurfaceVariant
            textColor = theme.colors.onSurfaceVariant
            borderColor = theme.colors.outline
        }
    }

    Column(
        horizontalAlignment = Alignment.Start,
        modifier            = modifier
            .fillMaxWidth()
            .bringIntoViewRequester(bringIntoViewRequester)
    ) {
        Box(
            modifier = Modifier
                .height(p56)
                .fillMaxWidth()
                .background(
                    color = when {
                        !enabled -> AppTheme.current.colors.surfaceContainerLowest
                        readOnly -> AppTheme.current.colors.surfaceContainer
                        else -> AppTheme.current.colors.surface
                    },
                    shape = RoundedCornerShape(p12)
                )
                .border(width = p1, color = borderColor, RoundedCornerShape(p12))
                .padding(start = startPadding, end = endPadding),
            contentAlignment = Alignment.CenterStart
        ) {
            BasicTextField(
                modifier        = textFieldModifier
                    .fillMaxWidth()
                    .padding(start = leadingIcon?.let { p0 } ?: p16, top = p10, end = trailingIcon?.let { p24 } ?: p0)
                    .offset(p0, p4)
                    .focusRequester(focusRequester)
                    .focusProperties { this.canFocus = canFocus }
                    .onFocusEvent {
                        isFocused = it.isFocused
                        onFocusChanged?.invoke(it.isFocused)
                        labelColor  = when {
                            !enabled                    -> theme.colors.onSurfaceVariant
                            it.isFocused && !isError    -> theme.colors.primary
                            isError                     -> defaultLightColorScheme.error
                            else                        -> theme.colors.onSurfaceVariant
                        }
                        borderColor = when {
                            !enabled                    -> theme.colors.outline
                            it.isFocused && !isError    -> theme.colors.primary
                            isError                     -> defaultLightColorScheme.error
                            else                        -> theme.colors.outline
                        }
                        textColor   = when {
                            !enabled    -> theme.colors.onSurfaceVariant
                            isError    -> defaultLightColorScheme.error
                            else       -> theme.colors.onSurface
                        }
                        cursorColor = if (it.isFocused && isError) defaultLightColorScheme.error else theme.colors.primary
                    },
                value             = value,
                onValueChange     = { onValueChange(it.copy(text = inputType?.let { type -> validateTextInput(type, it.text) } ?: it.text)) },
                readOnly          = readOnly,
                maxLines          = maxLines,
                enabled           = enabled,
                interactionSource = interactionSource,
                keyboardActions   = keyboardActions,
                keyboardOptions   = keyboardOptions,
                cursorBrush       = SolidColor(cursorColor),
                textStyle         = TextStyle(
                    fontFamily      = carosoft,
                    fontWeight      = FontWeight.Light,
                    fontSize        = 16.sp,
                    lineHeight      = 24.sp,
                    letterSpacing   = 0.4.sp,
                    color           = textColor.copy(alpha = alpha),
                    textAlign       = textAlign,
                ),
                decorationBox     = { innerTextField ->
                    OutlinedTextFieldDefaults.DecorationBox(
                        value                = value.text,
                        innerTextField       = innerTextField,
                        visualTransformation = VisualTransformation.None,
                        singleLine           = singleLine,
                        enabled              = enabled,
                        interactionSource    = interactionSource,
                        container            = { },
                        label                = { label?.also { Texts.Label(text = it, color = labelColor, modifier = Modifier.offset(p0, p4), textStyle = labelSize) } },
                        contentPadding       = PaddingValues(bottom = p18, top = topContentPadding),
                        placeholder          = placeholder,
                        prefix               = leadingIcon,
                    )
                },
                visualTransformation = visualTransformation
            )

            trailingIcon?.also {
                Box(
                    modifier = Modifier
                        .align(Alignment.CenterEnd)
                        .padding(top = p10)
                        .offset(p0, p4),
                    contentAlignment = Alignment.TopCenter,
                    content = { it() }
                )
            }
        }

        if (isError && errorText.isNotEmpty()) {
            Row(
                modifier    = Modifier.padding(start = p16, end = p16, top = p4),
                verticalAlignment = Alignment.CenterVertically
            ) {
                errorLeadingIcon?.let { icon ->
                    Image(
                        painter     = painterResource(icon),
                        modifier    = Modifier.size(p20),
                        colorFilter = ColorFilter.tint(AppTheme.current.colors.error)
                    )
                    Spacer(modifier = Modifier.size(p4))
                }
                Texts.Overline(
                    modifier    = Modifier,
                    text        = errorText,
                    color       = AppTheme.current.colors.error,
                    uppercased  = false
                )
            }
        }
    }
}

@Composable
fun DefaultTextField(
    modifier                : Modifier                      = Modifier,
    value                   : String                        = "",
    label                   : String                        = "",
    isError                 : Boolean                       = false,
    errorText               : String                        = "",
    leadingIcon             : @Composable (() -> Unit)?     = null,
    trailingIcon            : @Composable (() -> Unit)?     = null,
    backgroundColor         : Color                         = AppTheme.current.colors.surface,
    textColor               : Color?                        = null
) {
    val borderColor     = if (isError) AppTheme.current.colors.error else backgroundColor
    val borderWidth     = if (isError) p2 else p1
    val labelStyle      = if (value.isNotEmpty() || leadingIcon != null) AppTheme.current.typography.labelSmall else AppTheme.current.typography.bodyLarge

    Column(
        horizontalAlignment = Alignment.Start,
        modifier            = modifier.fillMaxWidth()
    ) {
        Row(
            modifier = Modifier
                .heightIn(min = p54)
                .fillMaxWidth()
                .background(
                    color = backgroundColor,
                    shape = RoundedCornerShape(p12)
                )
                .border(width = borderWidth, color = borderColor, RoundedCornerShape(p12))
                .padding(horizontal = p16),
            verticalAlignment   = Alignment.CenterVertically
        ) {

            Column(
                modifier            = Modifier.weight(1f),
                verticalArrangement = Arrangement.Center
            ) {
                if (label.isNotEmpty())
                    Text(
                        text        = label,
                        color       = AppTheme.current.colors.onSurfaceVariant,
                        overflow    = TextOverflow.Ellipsis,
                        modifier    = Modifier.padding(bottom = p2),
                        style       = labelStyle
                    )

                Row {

                    leadingIcon?.let { it() }

                    if (value.isNotEmpty())
                        Texts.BodyLarge(
                            modifier    = Modifier.fillMaxWidth().padding(start = leadingIcon?.let { p8 } ?: p0),
                            text        = value,
                            maxLines    = 1,
                            color       = textColor
                        )
                }
            }
            trailingIcon?.let { it() }
        }

        if (isError && errorText.isNotEmpty())
            Texts.Overline(
                modifier    = Modifier.padding(start = p16, top = p4),
                text        = errorText,
                color       = AppTheme.current.colors.error,
                uppercased  = false
            )
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun SearchTextField(
    modifier                : Modifier                  = Modifier,
    searchCallbacks         : UISearchCallbacks,
    searchHint              : String                    = stringResource(SR.strings.search_products_and_places),
    maxLines                : Int                       = Int.MAX_VALUE,
    isError                 : Boolean                   = false,
    backgroundColor         : Color                     = AppTheme.current.colors.surface,
    interactionSource       : MutableInteractionSource  = remember { MutableInteractionSource() },
    visualTransformation    : VisualTransformation      = VisualTransformation.None,
    keyboardOptions         : KeyboardOptions           = KeyboardOptions.Default
) {
    val theme                                   = AppTheme.current
    val keyboardController                      = LocalSoftwareKeyboardController.current
    val focusManager                            = LocalFocusManager.current
    val isFocused                               = interactionSource.collectIsFocusedAsState().value
    val borderColor                             = when {
        isError                                  -> theme.colors.error
        isFocused                                -> theme.colors.primary
        else                                     -> theme.colors.outline
    }
    val borderWidth                             = remember(isError) { if (isError) p2 else p1 }
    val focusRequester                          = remember { FocusRequester() }
    val searchState                             = searchCallbacks.state.searchText.collectAsState()
    val openSearch                              = searchCallbacks.state.openSearch.collectAsState()
    val trailingIconVisible                     = searchState.value.isNotEmpty()
    val searchFieldShape                        = RoundedCornerShape(p24)

    LaunchedEffect(openSearch.value){
        if(openSearch.value)
            focusRequester.requestFocus()
    }

    BasicTextField(
        modifier        = modifier
            .background(
                color = backgroundColor,
                shape = searchFieldShape
            )
            .height(p48)
            .focusRequester(focusRequester),
        value           = searchState.value,
        onValueChange   = searchCallbacks::onSearchTextChanged,
        singleLine      = remember(maxLines) { maxLines == 1 },
        maxLines        = maxLines,
        textStyle       = AppTheme.current.typography.bodyMedium,
        cursorBrush     = SolidColor(AppTheme.current.colors.onSurface),
        decorationBox = { innerTextField ->
            OutlinedTextFieldDefaults.DecorationBox(
                value                = searchState.value,
                innerTextField       = innerTextField,
                visualTransformation = VisualTransformation.None,
                singleLine           = true,
                enabled              = true,
                interactionSource    = interactionSource,
                contentPadding       = PaddingValues(start = p16, end = p12, top = p12, bottom = p12),
                leadingIcon          = {
                    Image(
                        painter     = painterResource(SRC.images.search_icon),
                        modifier    = Modifier.size(p24),
                        colorFilter = ColorFilter.tint(theme.colors.onSurfaceVariant)
                    )
                },
                container            = { Box(modifier = Modifier.border(BorderStroke(borderWidth, borderColor), searchFieldShape)) },
                trailingIcon         = {
                    if (trailingIconVisible)
                        Image(
                            painter     = painterResource(SRC.images.close_icon),
                            modifier    = Modifier.size(p20).noRippleClickable { searchCallbacks.onSearchTextChanged("") },
                            colorFilter = ColorFilter.tint(theme.colors.onSurfaceVariant)
                        )
                },
                placeholder          = {
                    Texts.BodyMedium(
                        text        = searchHint,
                        color       = theme.colors.onSurfaceVariant,
                        maxLines    = 1
                    )
                }
            )
        },
        visualTransformation    = visualTransformation,
        keyboardOptions         = keyboardOptions,
        keyboardActions         = KeyboardActions(
            onDone      = {
                if (searchCallbacks.onKeyboardAction(ImeAction.Done)) {
                    keyboardController?.hide()
                    focusManager.clearFocus()
                }
            },
            onSearch    = {
                if (searchCallbacks.onKeyboardAction(ImeAction.Search)) {
                    keyboardController?.hide()
                    focusManager.clearFocus()
                }
            },
            onGo        = {
                if (searchCallbacks.onKeyboardAction(ImeAction.Go)) {
                    keyboardController?.hide()
                    focusManager.clearFocus()
                }
            },
            onNext      = {
                if (searchCallbacks.onKeyboardAction(ImeAction.Next)) {
                    keyboardController?.hide()
                    focusManager.clearFocus()
                }
            },
            onPrevious  = {
                if (searchCallbacks.onKeyboardAction(ImeAction.Previous)) {
                    keyboardController?.hide()
                    focusManager.clearFocus()
                }
            },
            onSend      = {
                if (searchCallbacks.onKeyboardAction(ImeAction.Send)) {
                    keyboardController?.hide()
                    focusManager.clearFocus()
                }
            }
        )
    )
}

@Composable
fun CodeTextField(
    modifier                : Modifier                      = Modifier,
    value                   : String                        = "",
    codeSize                : Int,
    onValueChange           : (String) -> Unit              = {},
    keyboardOptions         : KeyboardOptions               = KeyboardOptions.Default,
    keyboardActions         : KeyboardActions               = KeyboardActions.Default,
    focusRequester          : FocusRequester?               = null
) {
    BasicTextField(
        value           = value,
        onValueChange   = onValueChange,
        modifier        = modifier.then(if (focusRequester != null) Modifier.focusRequester(focusRequester) else Modifier),
        keyboardActions = keyboardActions,
        keyboardOptions = keyboardOptions,
        decorationBox   = { innerTextField ->
            Box {
                // hide the inner text field as we are dwelling the text field ourselves
                CompositionLocalProvider(
                    LocalTextSelectionColors.provides(
                        TextSelectionColors(
                            Color.Transparent,
                            Color.Transparent
                        )
                    )
                ) {
                    Box(modifier = Modifier
                        .drawWithContent { }
                        .padding(vertical = p32)
                    ) {
                        innerTextField()
                    }
                }
                Row(
                    modifier                = Modifier.padding(vertical = p32),
                    horizontalArrangement   = Arrangement.spacedBy(p12)
                ) {
                    repeat(codeSize) { index ->
                        val hasDigit = index < value.length
                        val borderColor = if (hasDigit) AppTheme.current.colors.primary else AppTheme.current.colors.outlineVariant
                        Box(
                            modifier            = Modifier
                                .border(p1, borderColor, RoundedCornerShape(p12))
                                .size(p54)
                                .clip(RoundedCornerShape(p12))
                                .background(AppTheme.current.colors.surface),
                            contentAlignment    = Alignment.Center
                        ) {
                            Texts.HeadlineMedium(text = if (value.lastIndex < index) "" else value[index].toString())
                        }
                    }
                }
            }
        }
    )
}